import { useEffect } from "react";
import analyticsService from "../AnalyticsService";
import { DEBOUNCE_MS } from "../constants";

const INTERACTION_TRIGGER_POINT = "interaction_tracker";
const SCROLL_THRESHOLDS = [25, 50, 75, 90];

// Elements (or their descendants) carrying this attribute already report their
// own "ui_change" events (e.g. trackSelectInteraction/trackToggleInteraction),
// so the generic listener below must not double-report them.
const ANALYTICS_MANAGED_ATTR = "data-analytics-managed";

// Buttons/links can carry this attribute to have their associated value
// (e.g. a toggled state) included on the auto-tracked "ui_click" event.
const ANALYTICS_VALUE_ATTR = "data-analytics-value";

const SCROLL_TARGET_SELECTORS = [
  "#root",
  "#alldata",
  ".scrollableCategory",
  ".scrollableFilter",
  ".selectedFiltersBlockScroll",
  ".chat-bubble-scroll",
  ".sms-scroll-content",
  ".scroll-view",
  ".scrollToNotificationPanel",
  ".infinite-scroll-datatable-container",
  ".p-datatable-scrollable-body",
  ".p-datatable-scrollable-wrapper",
].join(", ");

const debounce = (fn, wait) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), wait);
  };
};

const getElementLabel = (target) => {
  if (!target) return "unknown";

  return (
    target.getAttribute("aria-label") ||
    target.getAttribute("name") ||
    target.getAttribute("id") ||
    target.textContent?.trim()?.slice(0, 60) ||
    target.tagName?.toLowerCase() ||
    "unknown"
  );
};

const getScrollDepth = (target) => {
  const scrollContainer =
    target instanceof Element
      ? target
      : document.scrollingElement || document.documentElement;

  const scrollTop = scrollContainer.scrollTop || globalThis.scrollY || 0;
  const docHeight = scrollContainer.scrollHeight - scrollContainer.clientHeight;

  if (docHeight <= 0) return 100;
  return Math.min(100, Math.round((scrollTop / docHeight) * 100));
};

const getScrollTargetName = (target) => {
  if (!(target instanceof Element)) return "window";

  const className =
    typeof target.className === "string" ? target.className.trim() : "";

  return target.id || className || target.tagName?.toLowerCase() || "unknown";
};

const isScrollableElement = (element) => {
  if (!(element instanceof HTMLElement)) return false;

  const style = globalThis.getComputedStyle?.(element);
  const overflowY = style?.overflowY || "";
  const overflowX = style?.overflowX || "";
  const isOverflowing =
    element.scrollHeight > element.clientHeight ||
    element.scrollWidth > element.clientWidth;

  return (
    (isOverflowing && /(auto|scroll|overlay)/i.test(overflowY)) ||
    /(auto|scroll|overlay)/i.test(overflowX)
  );
};

export const useInteractionTracker = () => {
  useEffect(() => {
    const handledNativeEvents = new WeakSet();

    const markHandled = (event) => {
      if (!event || typeof event !== "object") return false;
      if (handledNativeEvents.has(event)) return true;
      handledNativeEvents.add(event);
      return false;
    };

    const trackInteraction = (eventName, params = {}) => {
      analyticsService.track(
        eventName,
        {
          trigger_point: INTERACTION_TRIGGER_POINT,
          ...params,
        },
        { dedupe: false },
      );
    };

    const onClick = (event) => {
      if (markHandled(event)) return;

      const target = event.target?.closest(
        "button, a, [role='button'], [role='tab'], [role='menuitem']",
      );
      if (!target) return;

      const clickValue = target.getAttribute(ANALYTICS_VALUE_ATTR);

      trackInteraction("ui_click", {
        interaction_type: "clicked",
        element_type: target.tagName?.toLowerCase(),
        element_label: getElementLabel(target),
        element_id: target.getAttribute("id") || null,
        interaction_source: "mouse_click",
        ...(clickValue !== null ? { click_value: clickValue } : {}),
      });
    };

    const onChange = (event) => {
      if (markHandled(event)) return;

      const target = event.target;
      if (!target) return;
      if (target.closest?.(`[${ANALYTICS_MANAGED_ATTR}="true"]`)) return;

      const type = target.type || target.tagName?.toLowerCase();

      if (
        ["select-one", "select-multiple", "checkbox", "radio"].includes(type)
      ) {
        trackInteraction("ui_change", {
          interaction_type: "changed",
          input_type: type,
          input_name: getElementLabel(target),
          input_value: target.value,
          checked:
            typeof target.checked === "boolean" ? target.checked : undefined,
        });
      }
    };

    const onCopy = (event) => {
      if (markHandled(event)) return;

      trackInteraction("copy", {
        action: "copy",
        interaction_type: "copied",
      });
    };
    const onPaste = (event) => {
      if (markHandled(event)) return;

      trackInteraction("paste", {
        action: "paste",
        interaction_type: "pasted",
      });
    };
    const onPrint = (event) => {
      if (markHandled(event)) return;

      trackInteraction("print", {
        action: "print",
        interaction_type: "printed",
      });
    };

    const scrollThresholdsByTarget = new WeakMap();

    const trackScrollThresholds = (currentTarget) => {
      const scrollDepth = getScrollDepth(currentTarget);
      const scrollTarget = getScrollTargetName(currentTarget);
      const targetKey = currentTarget instanceof Element ? currentTarget : document.documentElement;
      const sentThresholds = scrollThresholdsByTarget.get(targetKey) || new Set();

      SCROLL_THRESHOLDS.forEach((threshold) => {
        if (scrollDepth < threshold || sentThresholds.has(threshold)) {
          return;
        }

        sentThresholds.add(threshold);
        trackInteraction("scroll_depth", {
          interaction_type: "scroll",
          scroll_depth: threshold,
          scroll_depth_actual: scrollDepth,
          scroll_target: scrollTarget,
        });
      });

      scrollThresholdsByTarget.set(targetKey, sentThresholds);
    };

    const onScroll = debounce((event) => {
      if (markHandled(event)) return;

      const currentTarget = event?.target || event?.currentTarget;
      trackScrollThresholds(currentTarget);
    }, DEBOUNCE_MS.SCROLL);

    const attachedTargets = new Set();
    let refreshTimer = null;

    const attachTarget = (element) => {
      if (!(element instanceof Element) || attachedTargets.has(element)) return;

      element.addEventListener("scroll", onScroll, { passive: true });
      attachedTargets.add(element);
    };

    const scanForScrollTargets = () => {
      const candidateTargets = new Set([
        document.scrollingElement,
        document.documentElement,
        document.body,
        ...document.querySelectorAll(SCROLL_TARGET_SELECTORS),
      ]);

      document.querySelectorAll("*").forEach((element) => {
        if (isScrollableElement(element)) {
          candidateTargets.add(element);
        }
      });

      candidateTargets.forEach(attachTarget);
    };

    const scheduleRefresh = () => {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(scanForScrollTargets, 0);
    };

    const mutationObserver = new MutationObserver(scheduleRefresh);

    globalThis.addEventListener("click", onClick, { passive: true });
    globalThis.addEventListener("change", onChange, { passive: true });
    globalThis.addEventListener("copy", onCopy);
    globalThis.addEventListener("paste", onPaste);
    globalThis.addEventListener("beforeprint", onPrint);
    document.addEventListener("scroll", onScroll, true);
    scanForScrollTargets();
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "id", "style"],
    });

    return () => {
      globalThis.removeEventListener("click", onClick);
      globalThis.removeEventListener("change", onChange);
      globalThis.removeEventListener("copy", onCopy);
      globalThis.removeEventListener("paste", onPaste);
      globalThis.removeEventListener("beforeprint", onPrint);
      document.removeEventListener("scroll", onScroll, true);
      mutationObserver.disconnect();
      clearTimeout(refreshTimer);

      attachedTargets.forEach((element) => {
        element.removeEventListener("scroll", onScroll);
      });
    };
  }, []);
};

// Prevents the global change listener above from double-reporting events
// that custom components (PrimeReact, etc.) already report explicitly.
const stopNativeEventPropagation = (event) => {
  const nativeEvent = event?.originalEvent || event?.nativeEvent;
  nativeEvent?.stopPropagation?.();
  nativeEvent?.stopImmediatePropagation?.();
};

// Spread this on a custom select/toggle component's root element so the
// generic change listener skips it (pairs with trackSelectInteraction/trackToggleInteraction).
export const analyticsManagedProps = { [ANALYTICS_MANAGED_ATTR]: "true" };

export const trackSelectInteraction = (event, value, overrides = {}) => {
  stopNativeEventPropagation(event);
  const target = event?.target ?? event;
  const input_name =
    target?.id || target?.name || getElementLabel(target?.element);

  analyticsService.track(
    "ui_change",
    {
      trigger_point: INTERACTION_TRIGGER_POINT,
      interaction_type: "changed",
      input_type: "select",
      input_name,
      input_value: value ?? "",
      ...overrides,
    },
    { dedupe: false },
  );
};

export const trackToggleInteraction = (event, checked, overrides = {}) => {
  stopNativeEventPropagation(event);
  const target = event?.target ?? event;
  const input_name =
    target?.id || target?.name || getElementLabel(target?.element);

  analyticsService.track(
    "ui_change",
    {
      trigger_point: INTERACTION_TRIGGER_POINT,
      interaction_type: "toggled",
      input_type: "toggle",
      input_name,
      input_value: checked ? "on" : "off",
      checked: Boolean(checked),
      ...overrides,
    },
    { dedupe: false },
  );
};

// For elements whose click handler calls event.stopPropagation(), which
// prevents the generic window-level click listener above from ever seeing
// the event. Call this manually from such handlers to still report "ui_click".
export const trackClickInteraction = (target, overrides = {}) => {
  if (!target) return;

  const clickValue = target.getAttribute?.(ANALYTICS_VALUE_ATTR);

  analyticsService.track(
    "ui_click",
    {
      trigger_point: INTERACTION_TRIGGER_POINT,
      interaction_type: "clicked",
      element_type: target.tagName?.toLowerCase(),
      element_label: getElementLabel(target),
      element_id: target.getAttribute?.("id") || null,
      interaction_source: "mouse_click",
      ...(clickValue != null ? { click_value: clickValue } : {}),
      ...overrides,
    },
    { dedupe: false },
  );
};

export default useInteractionTracker;
